import { lazy, Suspense } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { listKo, matchKo, type KoEntry } from '../data/index';
import { titleCase } from '../format';
import { t } from '../i18n';
import { demoData, HomeSections, HeroVisual } from '../site/Sections';
import { track } from './analytics';
import { AddressSearch } from './AddressSearch';
import { leadEnabled, WaitlistForm } from './LeadForm';
import { Tabs } from './Tabs';

const MapPicker = lazy(() => import('./MapPicker'));

export interface LandingError { code: string; message: string; ko?: number; st?: string; region?: string; covered?: KoEntry[] }

type Mode = 'address' | 'parcel' | 'map';
const MODES: { key: Mode; label: string }[] = [
  { key: 'address', label: t.site.search.tabAddress },
  { key: 'parcel', label: t.site.search.tabParcel },
  { key: 'map', label: t.site.search.tabMap },
];

export function Landing({ initialKo, initialParcel, error, onSubmit, onRetry }: {
  initialKo?: string; initialParcel?: string; error?: LandingError | null;
  onSubmit: (ko: number, parcel: string) => void; onRetry?: () => void;
}) {
  const [kos, setKos] = useState<KoEntry[]>([]);
  const [ko, setKo] = useState(initialKo ?? '');
  const [parcel, setParcel] = useState(initialParcel ?? '');
  const [local, setLocal] = useState<LandingError | null>(null);
  // a k.o./parcel coming back with an error opens the parcel tab, so the input is visible and kept
  const [mode, setMode] = useState<Mode>(initialKo || initialParcel ? 'parcel' : 'address');
  const errRef = useRef<HTMLDivElement>(null);
  useEffect(() => { listKo().then(setKos).catch(() => setKos([])); }, []);
  useEffect(() => {
    // show "1131 Marija Dobje" for a bare id coming from the URL
    if (/^\d+$/.test(ko.trim()) && kos.length) { const m = matchKo(kos, ko); if (m) setKo(`${m.id} ${titleCase(m.name)}`); }
  }, [kos]);
  const err = local ?? error;
  useEffect(() => { if (error) errRef.current?.scrollIntoView({ block: 'center' }); }, [error]);

  const submit = (e: Event) => {
    e.preventDefault();
    const k = matchKo(kos, ko);
    if (!k) { setLocal({ code: 'ko_unknown', message: t.err.koUnknown, covered: kos }); return; }
    if (!parcel.trim()) return;
    setLocal(null);
    onSubmit(k.id, parcel);
  };

  return (
    <main id="main" class="landing">
      <section class="hero" aria-labelledby="hero-title">
        <div class="hero-copy">
          <span class="eyebrow">{t.site.hero.eyebrow}</span>
          <h1 id="hero-title">{t.site.hero.title}</h1>
          <p class="lead">{t.site.hero.lead}</p>
          <div id="iskanje" class="searchcard">
            <h2 class="sr-only">{t.site.search.title}</h2>
            <Tabs idBase="find" label={t.site.search.tabsLabel} items={MODES} value={mode} onChange={(m) => { setMode(m); track('search_mode', { mode: m }); }} />
            <div id="find-panel" role="tabpanel" class="searchcard-body">
              {mode === 'address' && (
                <AddressSearch onParcel={onSubmit} hint={t.site.search.hintAddress} />
              )}
              {mode === 'parcel' && (
                <form class="search" onSubmit={submit}>
                  <label>{t.landing.ko}
                    <input list="ko-list" value={ko} placeholder={t.landing.koPlaceholder} autoComplete="off" required
                      onInput={(e) => { setKo((e.target as HTMLInputElement).value); setLocal(null); }} />
                    <datalist id="ko-list">{kos.map((k) => <option key={k.id} value={`${k.id} ${titleCase(k.name)}`} />)}</datalist>
                  </label>
                  <label>{t.landing.parcel}
                    <input value={parcel} placeholder={t.landing.parcelPlaceholder} autoComplete="off" required inputMode="text"
                      onInput={(e) => setParcel((e.target as HTMLInputElement).value)} />
                  </label>
                  <button class="btn primary" type="submit">{t.landing.submit}</button>
                  <p class="cap find-hint">{t.site.search.hintParcel}</p>
                </form>
              )}
              {mode === 'map' && (
                <Suspense fallback={<div class="picker"><div class="picker-map picker-loading"><span class="cap">{t.landing.mapLoading}</span></div><div class="picker-bar"><span class="cap">{t.landing.mapHint}</span></div></div>}><MapPicker kos={kos} onParcel={onSubmit} /></Suspense>
              )}
            </div>
            {err && <div ref={errRef}><ErrorBox err={err} onRetry={onRetry} /></div>}
            <Examples />
          </div>
          <ul class="trust" aria-label={t.landing.free}>{t.site.hero.trust.map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
        <HeroVisual />
      </section>
      <HomeSections />
    </main>
  );
}

function ErrorBox({ err, onRetry }: { err: LandingError; onRetry?: () => void }) {
  return (
    <div class="error" role="alert">
      <p><b>{err.message}</b></p>
      {err.code === 'ko_unknown' && err.covered && err.covered.length > 0 && (
        <p class="cap">{t.err.koCovered} {err.covered.map((k) => `${k.id} ${titleCase(k.name)}`).join(', ')}</p>
      )}
      {err.code === 'outside_region' && (
        <>
          {leadEnabled && <p class="cap">{t.err.outsideLead}</p>}
          <WaitlistForm region={err.region ?? ''} ko={err.ko ?? 0} parcel={err.st ?? ''} />
        </>
      )}
      {err.code === 'fetch' && onRetry && <button class="btn" type="button" onClick={onRetry}>{t.err.retry}</button>}
    </div>
  );
}

/** One-click example reports under the search: the demo parcel plus the examples found by scripts/demo-assets.ts. */
function Examples() {
  const D = demoData;
  const m = /Parcela (\S+), k\.o\. \d+ (.+)$/.exec(D.title);
  const items = [{ href: D.href, label: m ? `${m[2]} ${m[1]}` : D.title }, ...D.more.map((x) => ({ href: x.href, label: x.label.split(':')[0] }))];
  return (
    <p class="examples">
      <span>{t.site.search.examples}</span>
      {items.map((x) => <a key={x.href} href={x.href} onClick={() => track('example_click', { label: x.label })}>{x.label}</a>)}
    </p>
  );
}
